import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { skillService } from '../services/skillService';
import { llmService } from '../services/llmService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import type { SkillDto, SkillFilters } from '../types/skill';
import type { LlmProviderConfigDto } from '../types/llm';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * Project skills: upload zip bundles (must include SKILL.md) and delete.
 */
export const SkillsPage = () => {
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  const [skills, setSkills] = useState<SkillDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<SkillFilters>({
    pageNumber: 1,
    pageSize: 10,
    searchTerm: '',
    isActive: undefined,
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [providerConfigs, setProviderConfigs] = useState<LlmProviderConfigDto[]>([]);
  const [selectedProviderConfigId, setSelectedProviderConfigId] = useState('');

  const hostedConfigs = useMemo(
    () => providerConfigs.filter((c) => c.providerSupportsHostedSkills && c.isActive),
    [providerConfigs]
  );

  useEffect(() => {
    if (!currentTenant) return;
    llmService.getProviderConfigs().then(setProviderConfigs).catch(() => {});
  }, [currentTenant]);

  const loadSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await skillService.getSkills(filters);
      setSkills(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load skills';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, addNotification]);

  useEffect(() => {
    if (currentTenant && currentProject) {
      loadSkills();
    } else {
      setSkills([]);
      setTotalCount(0);
      setLoading(false);
    }
  }, [currentTenant, currentProject, loadSkills]);

  const handleDelete = useCallback(
    async (skill: SkillDto) => {
      if (!window.confirm(`Delete skill "${skill.name}" and all versions?`)) return;
      try {
        await skillService.deleteSkill(skill.id);
        addNotification('Skill deleted', 'success');
        loadSkills();
      } catch (err: any) {
        addNotification(err.response?.data?.message || 'Failed to delete skill', 'error');
      }
    },
    [addNotification, loadSkills]
  );

  const handleUpload = async () => {
    if (!uploadFile) {
      addNotification('Choose a .zip file containing SKILL.md', 'warning');
      return;
    }
    if (!selectedProviderConfigId) {
      addNotification('Select a target provider', 'warning');
      return;
    }
    setUploading(true);
    try {
      await skillService.createSkillFromZip(
        uploadFile,
        uploadCategory || undefined,
        selectedProviderConfigId
      );
      addNotification('Skill uploaded', 'success');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadCategory('');
      setSelectedProviderConfigId('');
      loadSkills();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const columns: DataTableColumn<SkillDto>[] = useMemo(
    () => [
      { id: 'name', label: 'Name', minWidth: 160, render: (row) => row.name },
      { id: 'slug', label: 'Slug', minWidth: 120, render: (row) => row.slug },
      {
        id: 'provider',
        label: 'Provider',
        width: 140,
        render: (row) => row.providerName ?? '—',
      },
      {
        id: 'versions',
        label: 'Versions',
        width: 120,
        render: (row) =>
          row.latestVersionNumber != null ? `v${row.latestVersionNumber}` : '—',
      },
      {
        id: 'active',
        label: 'Status',
        width: 100,
        render: (row) => (
          <Chip size="small" label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'success' : 'default'} />
        ),
      },
      {
        id: 'actions',
        label: '',
        width: 72,
        align: 'right',
        render: (row) => (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleDelete]
  );

  const contextMissing = !currentTenant || !currentProject;

  return (
    <>
      <PageListLayout
        title="Skills"
        breadcrumbs={[{ label: 'Skills' }]}
        loading={loading && !contextMissing}
        error={contextMissing ? 'Select a tenant and project to manage skills.' : error || undefined}
        onErrorDismiss={() => setError(null)}
        createButton={
          contextMissing
            ? undefined
            : {
                label: 'Upload skill (zip)',
                onClick: () => setUploadOpen(true),
                icon: <AddIcon />,
              }
        }
        filters={
          !contextMissing ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Search skills"
                size="small"
                value={filters.searchTerm ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, pageNumber: 1, searchTerm: e.target.value }))}
                sx={{ minWidth: 280 }}
              />
            </Box>
          ) : undefined
        }
        isEmpty={!contextMissing && skills.length === 0}
        emptyState={{
          title: 'No skills yet',
          description: 'Upload a .zip that includes SKILL.md, then attach skills to agents from the agent detail page.',
          action: contextMissing
            ? undefined
            : { label: 'Upload skill (zip)', onClick: () => setUploadOpen(true) },
        }}
        pagination={
          !contextMissing
            ? {
                page: filters.pageNumber ?? 1,
                totalPages: Math.max(1, Math.ceil(totalCount / (filters.pageSize ?? 10))),
                onChange: (page) => setFilters((f) => ({ ...f, pageNumber: page })),
              }
            : undefined
        }
      >
        {!contextMissing && <DataTable columns={columns} rows={skills} loading={false} />}

        <Alert severity="info" sx={{ mt: 3 }}>
          Zip your skill folder (must contain <strong>SKILL.md</strong> with YAML frontmatter: name, description).
          After upload, open an agent and use the <strong>Skills</strong> tab to attach this skill.
        </Alert>
      </PageListLayout>

      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload skill bundle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Target Provider</InputLabel>
              <Select
                value={selectedProviderConfigId}
                label="Target Provider"
                onChange={(e) => setSelectedProviderConfigId(e.target.value)}
              >
                {hostedConfigs.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.providerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {hostedConfigs.length === 0 && (
              <Alert severity="warning" variant="outlined">
                No provider configs with hosted skills support. Configure one under LLM Provider Settings.
              </Alert>
            )}
            <Button variant="outlined" component="label">
              Choose .zip file
              <input
                type="file"
                accept=".zip,application/zip"
                hidden
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {uploadFile && (
              <Typography variant="body2" color="text.secondary">
                {uploadFile.name}
              </Typography>
            )}
            <TextField
              label="Category (optional)"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadFile || !selectedProviderConfigId}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
